/** @jsxImportSource @emotion/react */
'use memo'

import { useState } from 'react'
import styled from '@emotion/styled'
import appOrigin from '@sportninja/common/constants/appOrigin'
import { ORG_REGISTRATIONS_PER_PAGE } from '@sportninja/common/features/organization/registration/constants'
import type { PagedList } from '@sportninja/common/hooks/usePagedList'
import type {
  OrgRegistration,
  OrgRegistrationTab,
} from '@sportninja/common/features/organization/registration/types'
import ShareModal from 'src/components/ShareModal'
import ShowMoreFooter from 'src/components/ts-components/ShowMoreFooter'
import { useInfiniteScroll } from 'src/hooks/useInfiniteScroll'
import OrgRegistrationCard from './OrgRegistrationCard'
import OrgRegistrationCardSkeleton from './OrgRegistrationCardSkeleton'
import OrgRegistrationEmptyState from './OrgRegistrationEmptyState'

interface Props {
  list: PagedList<OrgRegistration>
  status: OrgRegistrationTab
  onOpen: (registration: OrgRegistration) => void
}

/** Cards shown while the first page loads. */
const COLD_LOAD_CARDS = 3

/**
 * The registrations for the current tab and filters, loading the next page
 * as the end of the list scrolls into view. While a page is on its way a
 * skeleton card stands at the end; a failed page stops the automatic loading
 * and offers a retry instead, so a failing server isn't hit in a loop.
 */
const OrgRegistrationList = ({ list, status, onOpen }: Props) => {
  'use memo'

  // One moment for every card, so two cards never disagree about "today".
  const [now] = useState(() => new Date())
  const [shareTarget, setShareTarget] = useState<OrgRegistration | null>(null)

  const canLoadMore = list.hasNextPage && !list.nextPageFailed
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: list.fetchNextPage,
    hasMore: canLoadMore,
    isLoading: list.isFetchingNextPage,
  })

  if (list.isLoading && list.items.length === 0) {
    return (
      <Stack aria-busy='true'>
        {Array.from({ length: COLD_LOAD_CARDS }, (_, index) => (
          <OrgRegistrationCardSkeleton key={index} />
        ))}
      </Stack>
    )
  }

  if (list.items.length === 0) {
    return <OrgRegistrationEmptyState status={status} />
  }

  return (
    <Stack>
      {list.items.map((registration) => (
        <OrgRegistrationCard
          key={registration.id}
          registration={registration}
          now={now}
          onOpen={onOpen}
          onShare={setShareTarget}
        />
      ))}

      {canLoadMore && (
        <div ref={sentinelRef}>
          <OrgRegistrationCardSkeleton />
        </div>
      )}

      {(list.nextPageFailed || !list.hasNextPage) && (
        <ShowMoreFooter
          hasMore={false}
          onLoadMore={list.fetchNextPage}
          // Only once paging actually happened — a one-page list has no
          // "more" the reader was waiting for.
          showEndOfList={list.items.length > ORG_REGISTRATIONS_PER_PAGE}
          isError={list.nextPageFailed}
          onRetry={list.fetchNextPage}
        />
      )}

      {shareTarget && (
        <ShareModal
          isOpen
          onClose={() => setShareTarget(null)}
          entityType='registration'
          entityName={shareTarget.name}
          shareUrl={`${appOrigin}/registration/${shareTarget.scheduleId}`}
        />
      )}
    </Stack>
  )
}

export default OrgRegistrationList

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
